import axios from 'axios';
import authService from './authService';
import { API_BASE_URL } from './apiConfig';

const API = API_BASE_URL;

class AdminService {
  getHeaders() {
    const token = authService.getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  // Metrics
  async getMetrics() {
    const response = await axios.get(`${API}/admin/metrics`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Queues
  async getQueues() {
    const response = await axios.get(`${API}/admin/queues`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Submissions
  async getSubmissions(params = {}) {
    const response = await axios.get(`${API}/admin/submissions`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async approveSubmission(id, reason = null) {
    const response = await axios.post(`${API}/admin/submissions/${id}/approve`, 
      { reason },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async rejectSubmission(id, reason) {
    const response = await axios.post(`${API}/admin/submissions/${id}/reject`, 
      { reason },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // Funding
  async activateFunding(entityType, entityId, data) {
    const response = await axios.post(
      `${API}/admin/funding/${entityType}/${entityId}/activate`,
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // Books
  async getBooks(params = {}) {
    const response = await axios.get(`${API}/admin/books`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async updatePipeline(bookId, data) {
    const response = await axios.put(`${API}/admin/books/${bookId}/pipeline`, 
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getSuggestedNarrators(bookId) {
    const response = await axios.get(`${API}/admin/books/${bookId}/suggested-narrators`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async assignNarrator(bookId, data) {
    const response = await axios.post(`${API}/admin/books/${bookId}/assign-narrator`, 
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // Narrators
  async getNarrators(params = {}) {
    const response = await axios.get(`${API}/admin/narrators`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async verifyNarrator(id) {
    const response = await axios.post(`${API}/admin/narrators/${id}/verify`, {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getNarratorDemos(narratorId) {
    const response = await axios.get(`${API}/admin/narrators/${narratorId}/demos`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  getDemoStreamUrl(demoId) {
    const token = authService.getAccessToken();
    const encodedToken = encodeURIComponent(token || "");
    return `${API}/admin/narrator/demos/${demoId}/stream?token=${encodedToken}`;
  }

  async reviewDemo(demoId, data) {
    const response = await axios.post(`${API}/admin/narrator/demos/${demoId}/review`, 
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async reassignNarrator(projectId, data) {
    const response = await axios.post(`${API}/admin/projects/${projectId}/reassign-narrator`, 
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // QC Issues
  async getQCIssues(params = {}) {
    const response = await axios.get(`${API}/admin/qc/issues`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async resolveQCIssue(id, reason = null) {
    const response = await axios.put(`${API}/admin/qc/issues/${id}/resolve`, 
      { reason },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // Proofs
  async getProofs(params = {}) {
    const response = await axios.get(`${API}/admin/proofs`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async uploadProof(formData) {
    const response = await axios.post(`${API}/admin/proofs/upload`, formData, {
      headers: {
        ...this.getHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // Events
  async getEvents(params = {}) {
    const response = await axios.get(`${API}/admin/events`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async markEventRead(id) {
    const response = await axios.post(`${API}/admin/events/${id}/mark-read`, {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // Audit
  async getAuditLog(params = {}) {
    const response = await axios.get(`${API}/admin/audit`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Payments (reuse from paymentService for admin views)
  async getPaymentIntents(params = {}) {
    const response = await axios.get(`${API}/admin/payments/intents`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Settings
  async getSettings() {
    const response = await axios.get(`${API}/admin/settings`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getSetting(key) {
    const response = await axios.get(`${API}/admin/settings/${key}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async updateSetting(key, valueJson) {
    const response = await axios.put(`${API}/admin/settings/${key}`, 
      { key, value_json: valueJson },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async bulkUpdateSettings(settings) {
    const response = await axios.post(`${API}/admin/settings/bulk`, 
      settings,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async deleteSetting(key) {
    const response = await axios.delete(`${API}/admin/settings/${key}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async resetSettings() {
    const response = await axios.post(`${API}/admin/settings/reset`, {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }
}

export default new AdminService();
